<script setup>
import { ref } from 'vue';
import TextField from '@/components/Form/TextField.vue';
import DefaultButton from '@/components/Buttons/DefaultButton.vue';
import { modelDateValue, minReportDate, clampToMinReportDate } from '@/helpers';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const emit = defineEmits(['apply']);

const sd = ref(modelDateValue());
const ed = ref(modelDateValue());

const now = new Date();
const tabs = [
    { key: 'today', label: t('tabs.4') },
    { key: 'yesterday', label: t('tabs.5') },
    { key: 'currentMonth', label: t(`months.${now.getMonth()}`) },
    { key: 'previousMonth', label: t(`months.${(now.getMonth() + 11) % 12}`) },
];
const activeTab = ref(0);

const formatDate = (date) => {
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    return `${year}-${month}-${day}`;
};

const apply = () => {
    sd.value = clampToMinReportDate(sd.value);
    ed.value = clampToMinReportDate(ed.value);
    emit('apply', { sd: sd.value, ed: ed.value });
};

const setTab = (index) => {
    activeTab.value = index;
    const date = new Date();

    switch (tabs[index].key) {
        case 'today':
            sd.value = modelDateValue();
            ed.value = modelDateValue();
            break;
        case 'yesterday':
            date.setDate(date.getDate() - 1);
            sd.value = formatDate(date);
            ed.value = formatDate(date);
            break;
        case 'currentMonth':
            sd.value = formatDate(new Date(date.getFullYear(), date.getMonth(), 1));
            ed.value = formatDate(new Date(date.getFullYear(), date.getMonth() + 1, 0));
            break;
        case 'previousMonth':
            sd.value = formatDate(new Date(date.getFullYear(), date.getMonth() - 1, 1));
            ed.value = formatDate(new Date(date.getFullYear(), date.getMonth(), 0));
            break;
    }

    apply();
};
</script>

<template>
    <div>
        <form @submit.prevent="apply">
            <div class="px-4 py-2">
                <TextField
                    type="date"
                    v-model="sd"
                    :min="minReportDate()"
                    placeholder="Start Date"
                    class="w-full rounded-b-none"
                />
                <TextField
                    type="date"
                    v-model="ed"
                    :min="minReportDate()"
                    placeholder="End Date"
                    class="w-full border-t border-neutral-200 rounded-t-none"
                />
                <slot></slot>
                <div class="mt-2">
                    <DefaultButton
                        @click.prevent="apply"
                        class="w-full"
                    >
                        {{ $t('general.26') }}
                    </DefaultButton>
                </div>
            </div>
        </form>

        <div class="h-12 px-4 flex">
            <ul class="list-none flex items-center space-x-2">
                <li
                    v-for="(tab, i) in tabs"
                    :key="tab.key"
                    :class="['tab-item', { active: activeTab == i }]"
                    @click="setTab(i)"
                >
                    <span>{{ tab.label }}</span>
                </li>
            </ul>
        </div>
    </div>
</template>

<style>
.tab-item {
    @apply inline-flex items-center px-2 py-1 rounded-2xl cursor-pointer;
}

.tab-item span {
    @apply text-white text-sm;
}

.tab-item.active {
    background-color: var(--color-primary3);
}

.tab-item.active span {
    color: #000;
    font-weight: 700;
}
</style>
